import { useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import type { PortfolioImage } from '@/data/portfolio-data';

interface LightboxProps {
  images: PortfolioImage[];
  index: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

const Lightbox = ({ images, index, onClose, onPrev, onNext }: LightboxProps) => {
  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    },
    [onClose, onPrev, onNext]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [handleKey]);

  const img = images[index];
  if (!img) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-petrol/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Visualisation de l'image"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full bg-petrol-foreground/10 hover:bg-petrol-foreground/20 text-petrol-foreground flex items-center justify-center transition-colors"
        aria-label="Fermer"
      >
        <X size={22} strokeWidth={1.5} />
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-petrol-foreground/10 hover:bg-petrol-foreground/20 text-petrol-foreground flex items-center justify-center transition-colors"
        aria-label="Précédent"
      >
        <ChevronLeft size={24} strokeWidth={1.5} />
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-petrol-foreground/10 hover:bg-petrol-foreground/20 text-petrol-foreground flex items-center justify-center transition-colors"
        aria-label="Suivant"
      >
        <ChevronRight size={24} strokeWidth={1.5} />
      </button>

      <div
        className="relative max-w-6xl max-h-[85vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={img.src}
          alt={img.alt}
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
          className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl select-none"
        />
        <p className="mt-4 text-xs text-petrol-foreground/60 font-heading uppercase tracking-wider">
          {index + 1} / {images.length}
        </p>
      </div>
    </div>
  );
};

export default Lightbox;
